import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { alpha, useTheme } from "@mui/material/styles";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import { fetchAgendaAttendeeSchedule } from "../../api/client";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";

function unwrap(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

const DAY_START_HOUR = 7;
const DAY_END_HOUR = 21;
const SLOT_MINUTES = 30;

function buildSlots(day) {
  const slots = [];
  let cursor = day.startOf("day").hour(DAY_START_HOUR).minute(0).second(0);
  const end = day.startOf("day").hour(DAY_END_HOUR).minute(0).second(0);
  while (cursor.isBefore(end)) {
    slots.push(cursor);
    cursor = cursor.add(SLOT_MINUTES, "minute");
  }
  return slots;
}

function eventCoversSlot(event, slotStart, slotEnd) {
  const start = dayjs(String(event.start).replace(" ", "T"));
  const end = dayjs(String(event.end).replace(" ", "T"));
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return false;
  return start.isBefore(slotEnd) && end.isAfter(slotStart);
}

const PERSON_COLORS = ["#1976d2", "#7b1fa2", "#00897b", "#ef6c00", "#c62828", "#5d4037", "#455a64"];

export default function AgendaFindTimeDialog({
  open,
  onClose,
  attendees = [],
  organizerEmail = null,
  initialStart,
  durationMinutes = 60,
  excludeEventId = null,
  onPickSlot,
}) {
  const theme = useTheme();
  const [day, setDay] = useState(() => dayjs(initialStart || undefined).startOf("day"));

  useEffect(() => {
    if (!open) return;
    setDay(dayjs(initialStart || undefined).startOf("day"));
  }, [open, initialStart]);

  const from = day.startOf("day").format("YYYY-MM-DD HH:mm:ss");
  const to = day.endOf("day").format("YYYY-MM-DD HH:mm:ss");
  const attendeeKey = attendees.map((a) => a.email).join(",");

  const { data, isLoading, isError, error } = useQuery(
    ["agenda-attendee-schedule", from, to, attendeeKey, excludeEventId],
    async () =>
      unwrap(
        await fetchAgendaAttendeeSchedule({
          from,
          to,
          attendees,
          excludeEventId,
        })
      ) || { attendees: [] },
    { enabled: open && attendees.length > 0, staleTime: 30_000 }
  );

  const people = data?.attendees || [];
  const slots = useMemo(() => buildSlots(day), [day]);
  const durationSlots = Math.max(1, Math.round(durationMinutes / SLOT_MINUTES));

  const isSlotFreeForAll = (slotStart) => {
    const slotEnd = slotStart.add(durationMinutes, "minute");
    return people.every((person) => {
      if (person.detailLevel === "unknown") return true;
      return !(person.events || []).some((ev) => eventCoversSlot(ev, slotStart, slotEnd));
    });
  };

  const handlePick = (slotStart) => {
    if (!isSlotFreeForAll(slotStart)) return;
    const start = slotStart;
    const end = slotStart.add(durationMinutes, "minute");
    onPickSlot?.({
      start: start.format("YYYY-MM-DDTHH:mm"),
      end: end.format("YYYY-MM-DDTHH:mm"),
      allDay: false,
    });
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Encontrar horário
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Veja a disponibilidade dos convidados e escolha um horário livre para todos.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton size="small" onClick={() => setDay((d) => d.subtract(1, "day"))} aria-label="Dia anterior">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 140, textAlign: "center", textTransform: "capitalize" }}>
              {day.format("ddd, DD MMM")}
            </Typography>
            <IconButton size="small" onClick={() => setDay((d) => d.add(1, "day"))} aria-label="Próximo dia">
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        {!attendees.length ? (
          <Alert severity="info">Adicione convidados no compromisso para comparar agendas.</Alert>
        ) : isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : isError ? (
          <Alert severity="error">{getApiErrorMessage(error, "Não foi possível carregar as agendas.")}</Alert>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `72px repeat(${Math.max(people.length, 1)}, minmax(120px, 1fr))`,
                gap: 0.5,
                minWidth: 280 + people.length * 120,
              }}
            >
              <Box />
              {people.map((person, idx) => {
                const isOrganizer =
                  organizerEmail &&
                  String(person.email || "")
                    .trim()
                    .toLowerCase() === String(organizerEmail).trim().toLowerCase();
                return (
                  <Box key={person.email || idx} sx={{ px: 0.5, pb: 1 }}>
                    <Typography variant="caption" fontWeight={800} noWrap display="block">
                      {isOrganizer
                        ? person.name
                          ? `Você (${person.name})`
                          : "Você"
                        : person.name || person.email || "Convidado"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {isOrganizer
                        ? "Sua agenda"
                        : person.detailLevel === "full"
                          ? "Agenda compartilhada"
                          : person.detailLevel === "busy_only"
                            ? "Só ocupação"
                            : "Sem usuário local"}
                    </Typography>
                  </Box>
                );
              })}

              {slots.map((slot) => {
                const slotEnd = slot.add(SLOT_MINUTES, "minute");
                const free = isSlotFreeForAll(slot);
                const showLabel = slot.minute() === 0;
                return (
                  <React.Fragment key={slot.format("HH:mm")}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ height: 28, display: "flex", alignItems: "center", pr: 0.5 }}
                    >
                      {showLabel ? slot.format("HH:mm") : ""}
                    </Typography>
                    {people.map((person, pIdx) => {
                      const covering = (person.events || []).filter((ev) => eventCoversSlot(ev, slot, slotEnd));
                      const busy = covering.length > 0;
                      const color = PERSON_COLORS[pIdx % PERSON_COLORS.length];
                      const title =
                        person.detailLevel === "full" && covering[0]?.title
                          ? covering[0].title
                          : busy
                            ? "Ocupado"
                            : "Livre";
                      const canStartHere = free;
                      return (
                        <Box
                          key={`${person.email}-${slot.format("HH:mm")}`}
                          title={title}
                          onClick={() => canStartHere && handlePick(slot)}
                          role={canStartHere ? "button" : undefined}
                          sx={{
                            height: 28,
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: busy
                              ? alpha(color, person.detailLevel === "full" ? 0.45 : 0.28)
                              : alpha(theme.palette.success.main, 0.08),
                            cursor: canStartHere ? "pointer" : "default",
                            "&:hover": canStartHere
                              ? { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -1 }
                              : undefined,
                            overflow: "hidden",
                            px: 0.5,
                          }}
                        >
                          {busy && person.detailLevel === "full" && covering[0]?.title ? (
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ fontSize: 10, lineHeight: "26px", color: "common.white", fontWeight: 700 }}
                            >
                              {covering[0].title}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
              Clique em um horário livre para aplicar ao compromisso
              {durationSlots > 1 ? ` (${durationMinutes} min)` : ""}. Blocos coloridos são ocupados
              {people.some((p) => p.detailLevel === "busy_only")
                ? "; quando a agenda não está compartilhada, só aparece a ocupação."
                : "."}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
